import { api } from "../api";
import { escapeHtml } from "../moderation/format";
import type { Session } from "../session";
import { requireElement } from "./Hud";
import { isolateKeyboardFromGame } from "./keyboard";

/**
 * Lista de quem esta no Jardim, para qualquer jogador logado, com o dossie de
 * cada um. Resolve quando o painel fecha.
 */
/**
 * onDossier abre o dossie de alguem e resolve com true se a lista deve fechar
 * (a pessoa vai mandar mensagem ou editar o proprio dossie).
 */
export function showPlayersPanel(session: Session, onDossier: (id: string) => Promise<boolean>): Promise<void> {
  return new Promise((resolve) => {
    const { token, user: me } = session;
    const root = requireElement<HTMLDivElement>("overlay-root");
    const overlay = document.createElement("div");
    overlay.className = "overlay";
    overlay.innerHTML = `
      <div class="panel mod-panel">
        <div class="profile-header">
          <div>
            <h1>Jogadores</h1>
            <p class="sub mod-sub" id="pl-count">Carregando…</p>
          </div>
          <button type="button" id="pl-refresh" class="secondary small">Atualizar</button>
        </div>
        <p class="notice hidden" id="pl-notice"></p>
        <div class="field mod-search">
          <input id="pl-filter" type="search" placeholder="Filtrar por nome…" autocomplete="off" />
        </div>
        <ul class="mod-list" id="pl-list"></ul>
        <button type="button" id="pl-close" class="secondary">Fechar</button>
      </div>
    `;
    root.replaceChildren(overlay);

    const $ = <T extends HTMLElement>(sel: string) => overlay.querySelector(sel) as T;
    const list = $<HTMLUListElement>("#pl-list");
    const notice = $<HTMLParagraphElement>("#pl-notice");
    const filter = $<HTMLInputElement>("#pl-filter");
    let players: { id: string; name: string }[] = [];
    let dialogOpen = false;

    const setNotice = (text: string | null, level: "ok" | "error" = "ok") => {
      notice.className = `notice ${level}${text ? "" : " hidden"}`;
      notice.textContent = text ?? "";
    };

    const render = () => {
      const term = filter.value.trim().toLowerCase();
      const shown = players.filter((p) => !term || p.name.toLowerCase().includes(term));
      $<HTMLParagraphElement>("#pl-count").textContent =
        `${players.length} ${players.length === 1 ? "upLover" : "upLovers"} no Jardim`;
      list.innerHTML =
        shown
          .map(
            (p) => `
          <li class="mod-row"><div class="mod-row-main">
            <div class="mod-who"><strong>${escapeHtml(p.name)}</strong></div>
            <div class="mod-actions">
              ${p.id === me.id ? `<span class="mod-handle">você</span>` : ""}
              <button type="button" class="secondary small" data-dossier="${p.id}">Dossiê</button>
            </div>
          </div></li>`,
          )
          .join("") || `<li class="empty">${players.length ? "Ninguém com esse nome" : "Ninguém online"}</li>`;
    };

    const load = async () => {
      try {
        players = (await api.playersOnline(token)).players;
        render();
      } catch (error) {
        setNotice(error instanceof Error ? error.message : "Não foi possível carregar a lista.", "error");
      }
    };

    list.addEventListener("click", async (event) => {
      const dossierBtn = (event.target as HTMLElement).closest<HTMLButtonElement>("[data-dossier]");
      if (!dossierBtn?.dataset.dossier) return;
      dialogOpen = true;
      const leaving = await onDossier(dossierBtn.dataset.dossier);
      dialogOpen = false;
      if (leaving) close();
    });

    filter.addEventListener("input", render);
    isolateKeyboardFromGame(overlay);
    $<HTMLButtonElement>("#pl-refresh").addEventListener("click", () => {
      setNotice(null);
      void load();
    });

    const close = () => {
      document.removeEventListener("keydown", onKey);
      root.replaceChildren();
      resolve();
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !dialogOpen) {
        event.preventDefault();
        close();
      }
    };
    document.addEventListener("keydown", onKey);
    $<HTMLButtonElement>("#pl-close").addEventListener("click", close);

    void load();
  });
}
